<template>
  <div class="form-share-options">
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="5000" rounded="lg" location="top">
      <p class="text-center font-weight-bold mb-0 text-white">{{ snackbar.text }}</p>
    </v-snackbar>

    <v-card class="section-card rounded-lg mx-auto" elevation="0" width="100%" :max-width="dense ? undefined : 900">
      <div class="card-section-header">
        <div class="d-flex align-center justify-space-between flex-wrap ga-3">
          <div class="d-flex align-center ga-3">
            <v-avatar color="brand-color" variant="tonal" size="40" rounded="lg">
              <v-icon color="brand-color" size="18">mdi-share-variant</v-icon>
            </v-avatar>
            <div>
              <h2 class="section-title mb-1">Embed & Share Options</h2>
              <p class="body-text-sm mb-0">
                Choose how you want to embed your form or share it with others.
              </p>
            </div>
          </div>
          <v-btn @click="openHelpUrl(helpArticles.FORM_EMBED)" color="brand-color"
              variant="tonal" size="small" rounded="lg" class="text-none">
              Learn more
              <template #append>
                <v-icon icon="mdi-open-in-new" size="14" />
              </template>
            </v-btn>
        </div>
      </div>
      <v-card-text class="pa-0">
        <v-tabs v-model="tab" color="brand-color" slider-color="brand-color" class="px-6 mt-4 border-b">
          <v-tab v-for="item in tabs" :key="item.value" :value="item.value" color="brand-color"
            :class="['text-none text-left font-weight-semibold', item.value !== tab ? 'text-medium-emphasis' : '']">
            <v-icon start>{{ item.icon }}</v-icon>
            {{ item.text }}
          </v-tab>
        </v-tabs>
        <v-window v-model="tab" class="px-6 pb-6">
          <!-- direct embed code -->
          <v-window-item :value="EMBED_TAB.DIRECT_EMBED">
            <v-row class="mt-5">
              <v-col>
                <v-card class="code-snippet-wrapper" rounded="lg">
                  <v-card-text class="position-relative pa-4">
                    <v-btn icon="mdi-content-copy" title="Copy Direct Embed Code" size="small" color="grey"
                      class="position-absolute top-0 right-0 my-3 mx-2 code-snippet-copy" @click="copyCode()"></v-btn>
                    <v-sheet class="code-snippet-sheet">
                      <pre class="code-snippet-block"><code v-html="highlightedCode"></code></pre>
                    </v-sheet>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6" class="d-flex ga-4">
                <v-text-field v-if="!formPreview.fullWidthForm" variant="outlined" density="compact" type="number"
                  label="Width (PX)" :model-value="formPreview.width" @update:model-value="setIframeWidth"
                  :rules="$valid([{ rule: 'number' }, { rule: 'required' }])"></v-text-field>
                <v-switch label="Full width form" v-model="formPreview.fullWidthForm"
                  @update:model-value="setFullWidthToggle(formPreview.fullWidthForm)"></v-switch>
              </v-col>
            </v-row>
          </v-window-item>
          <!-- iframe embed code -->
          <v-window-item :value="EMBED_TAB.IFRAME">
            <v-row class="mt-5">
              <v-col>
                <v-card class="code-snippet-wrapper" rounded="lg">
                  <v-card-text class="position-relative pa-4">
                    <v-btn icon="mdi-content-copy" title="Copy Iframe Code" size="small" color="grey"
                      class="position-absolute top-0 right-0 my-3 mx-2 code-snippet-copy" @click="copyCode()"></v-btn>
                    <v-sheet class="code-snippet-sheet">
                      <pre class="code-snippet-block"><code v-html="highlightedCode"></code></pre>
                    </v-sheet>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row class="mt-5">
              <v-col cols="12" md="10" class="d-flex ga-4">
                <v-text-field variant="outlined" density="comfortable" type="number" label="Height (PX)"
                  v-model="formPreview.height" @update:model-value="setIframeHeight"
                  :rules="$valid([{ rule: 'number' }, { rule: 'required' }])"></v-text-field>
                <v-text-field variant="outlined" density="comfortable" type="number" label="Width (PX)"
                  v-model="formPreview.width" @update:model-value="setIframeWidth"
                  :rules="$valid([{ rule: 'number' }, { rule: 'required' }])"></v-text-field>
                <v-switch label="Enable shadow" v-model="formPreview.formShadow"
                  @update:model-value="setFormShadow(formPreview.formShadow)"></v-switch>
              </v-col>
            </v-row>
          </v-window-item>
          <!-- qr code -->
          <v-window-item :value="EMBED_TAB.QR_CODE">
            <v-row class="mt-5 justify-center">
              <v-col cols="12" class="d-flex flex-column align-center ga-4">
                <v-card class="qr-code-wrapper" rounded="lg" variant="outlined">
                  <canvas ref="qrCanvas"></canvas>
                </v-card>
                <p class="body-text-sm text-medium-emphasis text-center mb-0">
                  Scan this code to open the form, or download it to print or share.
                </p>
                <v-btn color="brand-color" prepend-icon="mdi-download" class="text-none font-weight-semibold"
                  @click="downloadQrCode">
                  Download QR Code
                </v-btn>
              </v-col>
            </v-row>
          </v-window-item>
          <!-- share link and social buttons -->
          <v-window-item :value="EMBED_TAB.LINK">
            <v-row class="mt-5">
              <v-col>
                <v-text-field label="Share link" variant="outlined" density="comfortable" hide-details
                  prepend-inner-icon="mdi-share" v-model="sharedLink" color="black" readonly>
                  <template v-slot:append-inner>
                    <v-icon color="black" @click="copyCode()" title="Copy Share Link">
                      mdi-content-copy
                    </v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row class="mb-3">
              <v-col class="d-flex justify-end ga-2">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <a v-bind="props" :href="'https://twitter.com/intent/tweet?text=' + encodeURIComponent(sharedLink)"
                      target="_blank" class="text-decoration-none">
                      <v-icon size="x-large" color="black" class="cursor-pointer">mdi-alpha-x-box</v-icon>
                    </a>
                  </template>
                  <span>Share on Twitter</span>
                </v-tooltip>

                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <a v-bind="props"
                      :href="'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(sharedLink)"
                      target="_blank" class="text-decoration-none">
                      <v-icon size="x-large" color="info" class="cursor-pointer">mdi-facebook</v-icon>
                    </a>
                  </template>
                  <span>Share on Facebook</span>
                </v-tooltip>

                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <a v-bind="props"
                      :href="'https://www.linkedin.com/shareArticle?mini=true&url=' + encodeURIComponent(sharedLink)"
                      target="_blank" class="text-decoration-none">
                      <v-icon size="x-large" color="brand-color" class="cursor-pointer">mdi-linkedin</v-icon>
                    </a>
                  </template>
                  <span>Share on LinkedIn</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-window-item>
          <!-- send by email -->
          <v-window-item :value="EMBED_TAB.EMAIL">
            <form-email-send-panel v-if="tab === EMBED_TAB.EMAIL && resolvedFormId" :form-id="resolvedFormId"
              :published-url="sharedLink" :form-title="form.title" />
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import utilityMixin from '@/mixins/utility'
import { mapGetters } from 'vuex'
import { helpArticles } from '@/shared/constants/help-articles'
import * as actionTypes from '@/store/action-types'
import config from '@/api/config'
import Prism from 'prismjs'
import { drawQrCode } from '@/utils/qrCode'
import FormEmailSendPanel from './FormEmailSendPanel'

const EMBED_TAB = {
  DIRECT_EMBED: 'direct-embed',
  IFRAME: 'iframe',
  QR_CODE: 'qr-code',
  LINK: 'link',
  EMAIL: 'email'
}

export default {
  mixins: [utilityMixin],
  name: 'FormShareOptions',
  components: { FormEmailSendPanel },
  props: {
    formId: {
      type: Number,
      default: null
    },
    // Kept so the iframe size settings stay under the same browser-storage key as the Share page.
    variantId: {
      type: [Number, String],
      default: null
    },
    initialTab: {
      type: String,
      default: null
    },
    // Inside a dialog the card has no max-width and fills the dialog's own width instead of capping at 900px.
    dense: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      helpArticles,
      EMBED_TAB,
      tab: EMBED_TAB.DIRECT_EMBED,
      tabs: [
        {
          icon: 'mdi-code-block-braces',
          text: 'Direct Embed',
          value: EMBED_TAB.DIRECT_EMBED,
        },
        {
          icon: 'mdi-application-braces-outline',
          text: 'Iframe',
          value: EMBED_TAB.IFRAME,
        },
        {
          icon: 'mdi-qrcode',
          text: 'QR Code',
          value: EMBED_TAB.QR_CODE,
        },
        {
          icon: 'mdi-link-variant',
          text: 'Link & Social',
          value: EMBED_TAB.LINK,
        },
        {
          icon: 'mdi-email-fast-outline',
          text: 'Email',
          value: EMBED_TAB.EMAIL,
        },
      ],
      recaptcha: {
        selectedKey: {},
        enable: false
      },
      snackbar: {
        show: false,
        text: null,
        color: null
      },
      formSetting: null
    }
  },
  mounted: function () {
    if (this.initialTab) this.tab = this.initialTab
    this.fetchForm()
    this.getIframeHeight()
    this.getIframeWidth()
    this.getFormShadow()
    this.getFullWidthToggle()
  },
  methods: {
    copyCode: function () {
      let text
      switch (this.tab) {
        case EMBED_TAB.DIRECT_EMBED:
          text = this.directEmbedCode
          break
        case EMBED_TAB.IFRAME:
          text = this.iframeCode
          break
        case EMBED_TAB.LINK:
          text = this.sharedLink
          break
        default:
          text = ''
      }

      navigator.clipboard.writeText(text).then(() => {
        let text = this.tab === EMBED_TAB.LINK ? 'Link' : 'Code'
        let message = text + ' copied to clipboard.'
        this.showSnackbar(message, false)
      }).catch(err => {
        this.showSnackbar('Failed to copy.', true)
      })
    },
    showSnackbar(text, isError) {
      this.snackbar = {
        show: true,
        text: text,
        color: isError ? 'primary' : 'success'
      }
    },
    fetchForm: function () {
      this.$store.dispatch(actionTypes.FETCH_FORM, {
        id: this.resolvedFormId
      }).then((response) => {
        this.fetchFormSetting(response?.data?.data?.id)
        this.$store.dispatch(actionTypes.FETCH_FORM_SUCCESS, response?.data?.data)
      }, (err) => {
        this.$store.dispatch(actionTypes.FETCH_FORM_ERROR, {
          formId: this.resolvedFormId
        })
      })
    },
    fetchFormSetting: function (formId) {
      this.$store.dispatch(actionTypes.FORM_FETCH_SETTING, {
        formId: formId
      }).then((response) => {
        this.formSetting = response?.data?.data
        this.recaptcha.enable = this.formSetting.enable_google_recaptcha
      })
    },
    getIframeHeight: function () {
      const compositeKey = this.dimensionsStorageKey

      const storedDimensions = localStorage.getItem(compositeKey)

      if (storedDimensions) {
        const dimensions = JSON.parse(storedDimensions)
        this.formPreview.height = dimensions.height
        return dimensions.height || 400
      } else {
        return this.formPreview.height || 400
      }
    },
    getIframeWidth: function () {
      const compositeKey = this.dimensionsStorageKey
      const storedDimensions = localStorage.getItem(compositeKey)
      if (storedDimensions) {
        const dimensions = JSON.parse(storedDimensions)
        this.formPreview.width = dimensions.width
        return dimensions.width || 500
      } else {
        return this.formPreview.width || 500
      }
    },
    setIframeHeight: function (value) {
      this.$store.dispatch('FORM_UPDATE_IFRAME_HEIGHT', {
        height: value
      })
    },
    setIframeWidth: function (value) {
      this.$store.dispatch('FORM_UPDATE_IFRAME_WIDTH', {
        width: value
      })
    },
    getFormShadow: function () {
      const compositeKey = this.dimensionsStorageKey
      const storedDimensions = localStorage.getItem(compositeKey)
      if (storedDimensions) {
        const dimensions = JSON.parse(storedDimensions)
        this.formPreview.formShadow = dimensions.formShadow
        return dimensions.formShadow || false
      } else {
        return this.formPreview.formShadow || false
      }
    },
    setFormShadow: function (value) {
      this.$store.dispatch('FORM_UPDATE_IFRAME_FORM_SHADOW', {
        formShadow: value
      })
    },
    getFullWidthToggle: function () {
      const compositeKey = this.dimensionsStorageKey
      const storedDimensions = localStorage.getItem(compositeKey)
      if (storedDimensions) {
        const dimensions = JSON.parse(storedDimensions)
        this.formPreview.fullWidthForm = dimensions.fullWidthForm
        return dimensions.fullWidthForm || false
      } else {
        return this.formPreview.fullWidthForm || false
      }
    },
    setFullWidthToggle: function (value) {
      this.$store.dispatch('FORM_FULL_WIDTH_FORM', {
        fullWidthForm: value
      })
    },
    generateQrCode: function () {
      if (!this.$refs.qrCanvas || !this.currentFormKey) return

      try {
        drawQrCode(this.$refs.qrCanvas, this.sharedLink)
      } catch (error) {
        this.showSnackbar('Failed to generate QR code.', true)
      }
    },
    downloadQrCode: function () {
      if (!this.$refs.qrCanvas) return

      const link = document.createElement('a')
      link.download = `qr-code-${this.currentFormKey}.png`
      link.href = this.$refs.qrCanvas.toDataURL('image/png')
      link.click()
    }
  },

  computed: {
    ...mapGetters([
      'formPreview',
      'currentFormKey'
    ]),
    sharedLink: function () {
      return config.formsUrl + '/' + this.currentFormKey
    },
    iframeSource: function () {
      return config.formsUrl + '/' + this.currentFormKey + '/iframe'
    },
    iframeCode: function () {
      let boxShadow = 'none'
      if (this.formPreview.formShadow) {
        boxShadow = '11px 17px 50px 0px #727eb721'
      }
      return `<script src="` + config.formsUrl + `/js/handler.js/` + this.currentFormKey + `"></scri` + `pt>
      <iframe data-leadgen-form-key="` + this.currentFormKey + `" onload="onLeadgenFormIframeLoad(this)" style="display:block;border:0px;width:100%;max-width:` + this.formPreview.width + `px;margin:auto;height:` + this.formPreview.height + `px;box-shadow:` + boxShadow + `;" frameborder="0" src="` + this.iframeSource + `" scrolling="auto"></iframe>`
    },
    directEmbedCode: function () {
      let str = '<script type="text/javascript" src="' + config.formsUrl + '/js/lf.min.js/' + this.currentFormKey + '" async></scri' + 'pt>'
      str += '<div id="leadgen-form-wrap-' + this.currentFormKey + '" style="' + this.codeStyle + '"><leadgen-form-' + this.currentFormKey + '></leadgen-form-' + this.currentFormKey + '></div>'
      return str
    },
    codeStyle: function () {
      let width = this.formPreview.width + 'px'
      if (this.formPreview.fullWidthForm) {
        width = '100%'
      }
      return 'display:block;border:0;width:100%;max-width:' + width + ';margin:0 auto;'
    },
    highlightedCode() {
      let text = this.tab === EMBED_TAB.DIRECT_EMBED ? this.directEmbedCode : this.iframeCode
      return Prism.highlight(text, Prism.languages.html, 'html')
    },
    resolvedFormId: function () {
      return this.formId || parseInt(this.$route.params.id)
    },
    dimensionsStorageKey: function () {
      const variantId = this.variantId ?? this.$route.params.variantId
      return `form_${this.resolvedFormId}_variant_${variantId}_dimensions`
    },
    form: function () {
      return this.$store.getters.getForm(this.resolvedFormId) || {}
    },
    currentFormKey: function () {
      return this.form.key || 0
    },
    apiOrigin: function () {
      return (new URL(config.basePath)).origin
    },
    fullWidthForm: {
      get() {
        return this.$store.state.fullWidthForm
      },
      set(value) {
        this.$store.dispatch('FORM_FULL_WIDTH_FORM', value)
      }
    }
  },
  watch: {
    fullWidthForm(newValue) {
      localStorage.setItem('FORM_FULL_WIDTH_FORM', JSON.stringify(newValue))
    },
    tab(newTab) {
      if (newTab === EMBED_TAB.QR_CODE) {
        this.$nextTick(this.generateQrCode)
      }
    },
    currentFormKey() {
      if (this.tab === EMBED_TAB.QR_CODE) {
        this.$nextTick(this.generateQrCode)
      }
    }
  }
}
</script>

<style scoped>
.code-snippet-copy {
  z-index: 1;
}

.qr-code-wrapper {
  display: inline-flex;
}
</style>
